import type { DialogHTMLAttributes, HTMLAttributes, ReactElement, ReactNode, Ref } from 'react';
import { createPortal } from 'react-dom';

type FrameContent = {
  readonly children: ReactNode;
  readonly close: ReactElement;
};

/** The native dialog owns the top layer and keyboard modality. Its content panel
 * scrolls independently: the close control is a sibling, never a panel child.
 * Keep the native ref so each caller retains its busy/dirty and focus guards. */
export function ModalDialog({
  children,
  close,
  className,
  ref,
  ...props
}: FrameContent &
  DialogHTMLAttributes<HTMLDialogElement> & {
    readonly ref?: Ref<HTMLDialogElement>;
  }): React.JSX.Element {
  return (
    <dialog {...props} ref={ref} className="tosho-modal-overlay">
      <div className={'tosho-modal-panel ' + (className ?? '')}>{children}</div>
      {close}
    </dialog>
  );
}

/** Custom dialogs escape transformed or scrolling application ancestors. The
 * accessible dialog includes both the panel and the overlay's close control;
 * existing Escape, backdrop and focus handlers remain owned by the caller.
 * Preserve the caller's module context class when controls inherit its theme
 * (client/provider directories, consolidation); shared CSS owns overlay geometry
 * and every close-button state, so module button rules cannot restore a frame. */
export function ModalBackdrop({
  children,
  close,
  className,
  ...props
}: FrameContent & HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  const overlay = (
    <div
      {...props}
      role="dialog"
      aria-modal="true"
      className={'tosho-modal-overlay tosho-modal-backdrop ' + (className ?? '')}
    >
      {children}
      {close}
    </div>
  );
  // Static rendering has no viewport. The browser always mounts into body.
  return typeof document === 'undefined' ? overlay : createPortal(overlay, document.body);
}
